/**
 * 审核日志表格组件
 *
 * 展示审核日志列表，支持筛选和详情查看。
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  safeguardAdminApi,
  type AuditLogQueryResult,
  type AuditLogDetail,
} from '../../services/safeguardAdminApi';

const SEVERITY_LABELS: Record<string, { label: string; color: string }> = {
  high: { label: '高危', color: 'bg-red-100 text-red-700' },
  medium: { label: '中危', color: 'bg-yellow-100 text-yellow-700' },
  low: { label: '低危', color: 'bg-blue-100 text-blue-700' },
  none: { label: '无风险', color: 'bg-gray-100 text-gray-700' },
};

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  block: { label: '拦截', color: 'bg-red-100 text-red-700' },
  warn: { label: '警告', color: 'bg-yellow-100 text-yellow-700' },
  allow: { label: '放行', color: 'bg-green-100 text-green-700' },
};

export const AuditLogTable: React.FC = () => {
  const [data, setData] = useState<AuditLogQueryResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState<AuditLogDetail | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  const [filters, setFilters] = useState({
    severity: '',
    action: '',
    source_endpoint: '',
    offset: 0,
  });

  const limit = 30;

  const fetchData = useCallback(async () => {
    try {
      const result = await safeguardAdminApi.queryLogs({
        severity: filters.severity || undefined,
        action: filters.action || undefined,
        source_endpoint: filters.source_endpoint || undefined,
        limit,
        offset: filters.offset,
      });
      setData(result);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取日志失败');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewDetail = async (logId: number) => {
    try {
      const d = await safeguardAdminApi.getLogDetail(logId);
      setDetail(d);
      setShowDetail(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取详情失败');
    }
  };

  const totalPages = data ? Math.ceil(data.total / limit) : 0;
  const currentPage = Math.floor(filters.offset / limit) + 1;

  return (
    <div className="space-y-4">
      {/* 筛选栏 */}
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={filters.severity}
          onChange={e => setFilters(f => ({ ...f, severity: e.target.value, offset: 0 }))}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white"
        >
          <option value="">全部风险等级</option>
          <option value="high">高危</option>
          <option value="medium">中危</option>
          <option value="low">低危</option>
        </select>

        <select
          value={filters.action}
          onChange={e => setFilters(f => ({ ...f, action: e.target.value, offset: 0 }))}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white"
        >
          <option value="">全部动作</option>
          <option value="block">拦截</option>
          <option value="warn">警告</option>
          <option value="allow">放行</option>
        </select>

        <select
          value={filters.source_endpoint}
          onChange={e => setFilters(f => ({ ...f, source_endpoint: e.target.value, offset: 0 }))}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white"
        >
          <option value="">全部来源</option>
          <option value="chat">Chat</option>
          <option value="workflow">Workflow</option>
          <option value="agent_team">AgentTeam</option>
        </select>

        <button
          onClick={() => { setLoading(true); fetchData(); }}
          className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          刷新
        </button>

        {data && (
          <span className="text-xs text-gray-400">共 {data.total} 条</span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      )}

      {/* 日志表格 */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-3 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : data && data.items.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">时间</th>
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">用户</th>
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">来源</th>
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">方向</th>
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">风险</th>
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">动作</th>
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">触发词</th>
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">云端审核</th>
                <th className="text-left py-2.5 px-3 text-gray-500 font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item, idx) => (
                <tr
                  key={item.id}
                  className={`border-b border-gray-50 hover:bg-gray-50 cursor-pointer ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                  onClick={() => handleViewDetail(item.id)}
                >
                  <td className="py-2 px-3 text-gray-500 text-xs whitespace-nowrap">
                    {item.created_at ? new Date(item.created_at).toLocaleString('zh-CN') : '-'}
                  </td>
                  <td className="py-2 px-3">
                    <div className="text-xs">{item.user_nickname || item.user_email || '-'}</div>
                  </td>
                  <td className="py-2 px-3 text-xs">{item.source_endpoint || '-'}</td>
                  <td className="py-2 px-3 text-xs">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      item.check_direction === 'input' ? 'bg-orange-100 text-orange-700' : 'bg-cyan-100 text-cyan-700'
                    }`}>
                      {item.check_direction === 'input' ? '输入' : '输出'}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${SEVERITY_LABELS[item.severity]?.color || ''}`}>
                      {SEVERITY_LABELS[item.severity]?.label || item.severity}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${ACTION_LABELS[item.action]?.color || ''}`}>
                      {ACTION_LABELS[item.action]?.label || item.action}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-xs text-gray-600 max-w-[200px] truncate">
                    {item.matched_words.length > 0 ? item.matched_words.join(', ') : '-'}
                  </td>
                  <td className="py-2 px-3 text-xs">
                    {item.check_type === 'cloud' ? (
                      <span className="text-purple-600">是 ({item.sensitive_level || '-'})</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <button className="text-blue-500 hover:text-blue-700 text-xs">详情</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center text-gray-400 py-8">暂无审核日志</div>
      )}

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">
            第 {currentPage} / {totalPages} 页
          </span>
          <div className="flex gap-2">
            <button
              disabled={filters.offset === 0}
              onClick={() => setFilters(f => ({ ...f, offset: Math.max(0, f.offset - limit) }))}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              上一页
            </button>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setFilters(f => ({ ...f, offset: f.offset + limit }))}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下一页
            </button>
          </div>
        </div>
      )}

      {/* 详情弹窗 */}
      {showDetail && detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowDetail(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-medium">审核日志详情 #{detail.id}</h3>
              <button onClick={() => setShowDetail(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-4 space-y-3">
              <DetailRow label="时间" value={detail.created_at ? new Date(detail.created_at).toLocaleString('zh-CN') : '-'} />
              <DetailRow label="审核类型" value={detail.check_type === 'cloud' ? '云端审核' : '本地审核'} />
              <DetailRow label="审核方向" value={detail.check_direction === 'input' ? '输入' : '输出'} />
              <DetailRow label="来源" value={detail.source_endpoint || '-'} />
              <DetailRow label="用户" value={detail.user_nickname ? `${detail.user_nickname} (${detail.user_email})` : (detail.user_email || '-')} />
              <DetailRow label="风险等级" value={SEVERITY_LABELS[detail.severity]?.label || detail.severity} />
              <DetailRow label="处理动作" value={ACTION_LABELS[detail.action]?.label || detail.action} />
              <DetailRow label="匹配词" value={detail.matched_words.length > 0 ? detail.matched_words.join(', ') : '-'} />
              <DetailRow label="词库分类" value={detail.categories.length > 0 ? detail.categories.join(', ') : '-'} />

              {detail.check_type === 'cloud' && (
                <>
                  <div className="border-t border-gray-100 pt-3 mt-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">云端审核结果</h4>
                  </div>
                  <DetailRow label="敏感等级" value={detail.sensitive_level || '-'} />
                  <DetailRow label="风险等级" value={detail.alibaba_risk_level || '-'} />
                  <DetailRow label="响应码" value={detail.alibaba_response_code != null ? String(detail.alibaba_response_code) : '-'} />
                </>
              )}

              {detail.trigger_content && (
                <div className="border-t border-gray-100 pt-3 mt-3">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">触发内容</h4>
                  <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                    {detail.trigger_content}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-start gap-3 text-sm">
    <span className="text-gray-400 w-24 flex-shrink-0">{label}</span>
    <span className="text-gray-700">{value}</span>
  </div>
);
